import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, FileText, Settings, ClipboardCheck, Edit2, Wrench, Printer, Trash2, Save, GripVertical, X, Image, Upload, Eye, Lock, CheckCircle2, Copy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { productService, productRevisionService, workCycleService, workPhaseService, controlPlanService, attachmentService, revisionToolService, toolService as toolSvc, machineService, productConfigurationService, toolListService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FileUploadZone } from '../components/FileUploadZone';
import PrintableToolList from '../components/PrintableToolList';
import { useReactToPrint } from 'react-to-print';

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [workCycles, setWorkCycles] = useState([]);
  const [workPhases, setWorkPhases] = useState([]);
  const [controlPlans, setControlPlans] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [revisionTools, setRevisionTools] = useState([]);
  const [machines, setMachines] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  const [productToolLists, setProductToolLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeRevision, setActiveRevision] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const toolListPrintRef = useRef(null);
  const handlePrintToolList = useReactToPrint({ contentRef: toolListPrintRef, documentTitle: `ToolList_${product?.code || ''}_${activeRevision?.revision_code || ''}` });
  const { hasRole } = useAuth();

  // Dialog states
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [cycleDialogOpen, setCycleDialogOpen] = useState(false);
  const [phaseDialogOpen, setPhaseDialogOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [toolListDialogOpen, setToolListDialogOpen] = useState(false);

  const [revisionForm, setRevisionForm] = useState({
    revision_code: '',
    description: '',
    revision_date: new Date().toISOString().split('T')[0],
    is_active: false
  });

  const [cycleForm, setCycleForm] = useState({
    code: '',
    description: ''
  });

  const [phaseForm, setPhaseForm] = useState({
    phase_number: 10,
    description: '',
    department: '',
    machine_id: '',
    standard_time_minutes: '',
    equipment: '',
    critical_tools: '',
    required_control: ''
  });

  const [selectedCycleId, setSelectedCycleId] = useState(null);

  const [configForm, setConfigForm] = useState({ drawing_revision: '', description: '', work_cycle_id: '', control_plan_id: '', tool_list_id: '', process_flow: '' });
  const [toolListForm, setToolListForm] = useState({ description: '' });
  const [allProductCycles, setAllProductCycles] = useState([]);

  // Inline phase editing
  const [editingPhaseId, setEditingPhaseId] = useState(null);
  const [editPhaseForm, setEditPhaseForm] = useState({});
  const [phaseSaving, setPhaseSaving] = useState(false);

  // Inline tool editing
  const [editingToolId, setEditingToolId] = useState(null);
  const [editToolForm, setEditToolForm] = useState({});
  const [newToolRow, setNewToolRow] = useState(null);
  const [toolSaving, setToolSaving] = useState(false);
  const [drawingPreview, setDrawingPreview] = useState(null);
  const drawingInputRef = useRef(null);

  useEffect(() => {
    loadProduct();
    machineService.getAll().then(r => setMachines(r.data.filter(m => m.status !== 'dismessa'))).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (activeRevision) {
      loadWorkCycles(activeRevision.id);
      loadControlPlans(activeRevision.id);
      loadRevisionTools(activeRevision.id);
    }
  }, [activeRevision]);

  const loadProduct = async () => {
    try {
      const [productRes, revisionsRes, attachmentsRes] = await Promise.all([
        productService.getById(id),
        productRevisionService.getAll({ product_id: id }),
        attachmentService.getAll({ entity_type: 'product', entity_id: id })
      ]);
      setProduct(productRes.data);
      setRevisions(revisionsRes.data);
      setAttachments(attachmentsRes.data);
      
      // Load configurations and tool lists for this product
      productConfigurationService.getAll({ product_id: id }).then(r => setConfigurations(r.data)).catch(() => {});
      toolListService.getAll({ product_id: id }).then(r => setProductToolLists(r.data)).catch(() => {});

      const active = revisionsRes.data.find(r => r.is_active);
      if (active) setActiveRevision(active);
      else if (revisionsRes.data.length > 0) setActiveRevision(revisionsRes.data[0]);
    } catch (error) {
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const loadWorkCycles = async (revisionId) => {
    try {
      const response = await workCycleService.getAll({ product_revision_id: revisionId });
      setWorkCycles(response.data);
      if (response.data.length > 0) {
        setSelectedCycleId(response.data[0].id);
        loadWorkPhases(response.data[0].id);
      } else {
        setWorkPhases([]);
      }
    } catch (error) {
      console.error('Failed to load work cycles');
    }
  };

  const loadWorkPhases = async (cycleId) => {
    try {
      const response = await workPhaseService.getAll({ work_cycle_id: cycleId });
      setWorkPhases(response.data);
    } catch (error) {
      console.error('Failed to load work phases');
    }
  };

  const loadControlPlans = async (revisionId) => {
    try {
      const response = await controlPlanService.getAll({ product_revision_id: revisionId });
      setControlPlans(response.data);
    } catch (error) {
      console.error('Failed to load control plans');
    }
  };

  const loadRevisionTools = async (revisionId) => {
    try {
      const response = await revisionToolService.getAll({ product_revision_id: revisionId });
      setRevisionTools(response.data);
    } catch (error) {
      console.error('Failed to load revision tools');
    }
  };

  // Inline tool CRUD
  const startEditTool = (tool) => {
    setEditingToolId(tool.id);
    setEditToolForm({
      sequence: tool.sequence,
      tool_code: tool.tool_code || '',
      tool_description: tool.tool_description || '',
      work_phase_id: tool.work_phase_id || '',
      height: tool.height ?? '',
      radius: tool.radius ?? '',
      notes: tool.notes || '',
    });
  };

  const cancelEditTool = () => {
    setEditingToolId(null);
    setEditToolForm({});
  };

  const saveEditTool = async () => {
    setToolSaving(true);
    try {
      const payload = {
        ...editToolForm,
        sequence: parseInt(editToolForm.sequence) || 10,
        height: editToolForm.height !== '' ? parseFloat(editToolForm.height) : null,
        radius: editToolForm.radius !== '' ? parseFloat(editToolForm.radius) : null,
        work_phase_id: editToolForm.work_phase_id || null,
      };
      // Set phase label
      if (payload.work_phase_id) {
        const phase = workPhases.find(p => p.id === payload.work_phase_id);
        payload.work_phase_label = phase ? phase.description : null;
      }
      await revisionToolService.update(editingToolId, payload);
      toast.success('Utensile aggiornato');
      setEditingToolId(null);
      loadRevisionTools(activeRevision.id);
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setToolSaving(false);
    }
  };

  const deleteTool = async (toolId) => {
    if (!window.confirm('Eliminare questo utensile dalla lista?')) return;
    try {
      await revisionToolService.delete(toolId);
      toast.success('Utensile rimosso');
      loadRevisionTools(activeRevision.id);
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };

  const startAddTool = () => {
    const maxSeq = revisionTools.length > 0 ? Math.max(...revisionTools.map(t => t.sequence)) : 0;
    setNewToolRow({
      sequence: maxSeq + 10,
      tool_code: '',
      tool_description: '',
      work_phase_id: '',
      height: '',
      radius: '',
      notes: '',
    });
  };

  const saveNewTool = async () => {
    if (!newToolRow.tool_code && !newToolRow.tool_description) {
      toast.error('Inserire almeno codice o descrizione utensile');
      return;
    }
    setToolSaving(true);
    try {
      const payload = {
        product_revision_id: activeRevision.id,
        sequence: parseInt(newToolRow.sequence) || 10,
        tool_code: newToolRow.tool_code || null,
        tool_description: newToolRow.tool_description || null,
        work_phase_id: newToolRow.work_phase_id || null,
        height: newToolRow.height !== '' ? parseFloat(newToolRow.height) : null,
        radius: newToolRow.radius !== '' ? parseFloat(newToolRow.radius) : null,
        notes: newToolRow.notes || null,
      };
      if (payload.work_phase_id) {
        const phase = workPhases.find(p => p.id === payload.work_phase_id);
        payload.work_phase_label = phase ? phase.description : null;
      }
      await revisionToolService.create(payload);
      toast.success('Utensile aggiunto');
      setNewToolRow(null);
      loadRevisionTools(activeRevision.id);
    } catch (error) {
      toast.error('Errore nell\'aggiunta');
    } finally {
      setToolSaving(false);
    }
  };

  const handleDrawingUpload = async (toolId, file) => {
    try {
      await revisionToolService.uploadDrawing(toolId, file);
      toast.success('Disegno caricato');
      loadRevisionTools(activeRevision.id);
    } catch (error) {
      toast.error('Errore nel caricamento disegno');
    }
  };

  const handleDrawingDelete = async (toolId) => {
    if (!window.confirm('Rimuovere il disegno?')) return;
    try {
      await revisionToolService.deleteDrawing(toolId);
      toast.success('Disegno rimosso');
      loadRevisionTools(activeRevision.id);
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };



  const handleCreateRevision = async (e) => {
    e.preventDefault();
    try {
      await productRevisionService.create({
        product_id: id,
        ...revisionForm
      });
      toast.success('Revision created');
      setRevisionDialogOpen(false);
      setRevisionForm({ revision_code: '', description: '', revision_date: new Date().toISOString().split('T')[0], is_active: false });
      loadProduct();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create revision');
    }
  };

  const handleCreateCycle = async (e) => {
    e.preventDefault();
    try {
      await workCycleService.create({
        product_revision_id: activeRevision.id,
        ...cycleForm
      });
      toast.success('Work cycle created');
      setCycleDialogOpen(false);
      setCycleForm({ code: '', description: '' });
      loadWorkCycles(activeRevision.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create work cycle');
    }
  };

  const handleCreatePhase = async (e) => {
    e.preventDefault();
    try {
      const data = {
        work_cycle_id: selectedCycleId,
        ...phaseForm,
        phase_number: parseInt(phaseForm.phase_number),
        standard_time_minutes: phaseForm.standard_time_minutes ? parseInt(phaseForm.standard_time_minutes) : null,
        display_order: workPhases.length
      };
      await workPhaseService.create(data);
      toast.success('Work phase created');
      setPhaseDialogOpen(false);
      setPhaseForm({ phase_number: (workPhases.length + 1) * 10, description: '', department: '', machine_id: '', standard_time_minutes: '', equipment: '', critical_tools: '', required_control: '' });
      loadWorkPhases(selectedCycleId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create work phase');
    }
  };

  const startEditPhase = (phase) => {
    let machineId = phase.machine_id || '';
    // Try to match default_machine text to an actual machine if no machine_id
    if (!machineId && phase.default_machine && machines.length > 0) {
      const match = machines.find(m =>
        phase.default_machine.includes(m.machine_code) || phase.default_machine.includes(m.machine_name)
      );
      if (match) machineId = match.id;
    }
    setEditingPhaseId(phase.id);
    setEditPhaseForm({
      phase_number: phase.phase_number,
      description: phase.description || '',
      department: phase.department || '',
      machine_id: machineId,
      standard_time_minutes: phase.standard_time_minutes ?? '',
      required_control: phase.required_control || '',
    });
  };

  const cancelEditPhase = () => {
    setEditingPhaseId(null);
    setEditPhaseForm({});
  };

  const saveEditPhase = async () => {
    if (!editPhaseForm.description) {
      toast.error('La descrizione è obbligatoria');
      return;
    }
    setPhaseSaving(true);
    try {
      await workPhaseService.update(editingPhaseId, {
        ...editPhaseForm,
        phase_number: parseInt(editPhaseForm.phase_number) || 10,
        standard_time_minutes: editPhaseForm.standard_time_minutes !== '' ? parseInt(editPhaseForm.standard_time_minutes) : null,
        machine_id: editPhaseForm.machine_id || null,
      });
      toast.success('Fase aggiornata');
      setEditingPhaseId(null);
      loadWorkPhases(selectedCycleId);
    } catch {
      toast.error('Errore aggiornamento fase');
    } finally {
      setPhaseSaving(false);
    }
  };

  const deletePhase = async (phaseId) => {
    if (!window.confirm('Eliminare questa fase di lavoro?')) return;
    try {
      await workPhaseService.delete(phaseId);
      toast.success('Fase eliminata');
      loadWorkPhases(selectedCycleId);
    } catch {
      toast.error('Errore eliminazione fase');
    }
  };

  const handleSetActiveRevision = async (revision) => {
    try {
      await productRevisionService.update(revision.id, { is_active: true });
      toast.success('Revision set as active');
      loadProduct();
    } catch (error) {
      toast.error('Failed to update revision');
    }
  };

  const loadAttachments = async () => {
    try {
      const res = await attachmentService.getAll({ entity_type: 'product', entity_id: id });
      setAttachments(res.data);
    } catch {}
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Product not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="product-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Link to="/products">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">{product.code}</h1>
              <Badge className={product.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                {product.status}
              </Badge>
            </div>
            <p className="text-slate-500 mt-1">{product.name}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="info">Info Prodotto</TabsTrigger>
          <TabsTrigger value="revisions">Revisioni</TabsTrigger>
          <TabsTrigger value="work-cycle">Ciclo di Lavoro</TabsTrigger>
          <TabsTrigger value="control-plans">Piani di Controllo</TabsTrigger>
          <TabsTrigger value="tools">Utensili ({revisionTools.length})</TabsTrigger>
          <TabsTrigger value="configurations" data-testid="tab-configurations">Configurazioni ({configurations.length})</TabsTrigger>
          <TabsTrigger value="attachments">Allegati ({attachments.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">General Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Code</p>
                    <p className="font-mono font-medium">{product.code}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Client</p>
                    <p className="font-medium">{product.client_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Family</p>
                    <p>{product.product_family || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Unita di Misura</p>
                    <p>{product.unit_of_measure}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Prezzo Unitario</p>
                    <p className="font-semibold text-emerald-700">{product.unit_price != null ? `${product.unit_price.toFixed(2)} EUR` : '-'}</p>
                  </div>
                </div>
                {product.description && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Description</p>
                    <p className="text-sm">{product.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Material Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Material</p>
                    <p>{product.material || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Raw Material Code</p>
                    <p className="font-mono">{product.raw_material_code || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">External Treatment</p>
                    <p>{product.external_treatment || '-'}</p>
                  </div>
                </div>
                {product.technical_notes && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Technical Notes</p>
                    <p className="text-sm">{product.technical_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {activeRevision && (
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-base">Active Revision: {activeRevision.revision_code}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Revision Date</p>
                      <p>{activeRevision.revision_date}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Description</p>
                      <p>{activeRevision.description || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="revisions" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Product Revisions</CardTitle>
              {hasRole('admin', 'quality') && (
                <Button size="sm" onClick={() => setRevisionDialogOpen(true)} data-testid="add-revision-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Revision
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {revisions.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No revisions yet</p>
              ) : (
                <div className="space-y-3">
                  {revisions.map((rev) => (
                    <div 
                      key={rev.id} 
                      className={`flex items-center justify-between p-4 rounded-lg border ${rev.is_active ? 'border-blue-200 bg-blue-50' : 'border-slate-200'}`}
                    >
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="font-mono font-medium">{rev.revision_code}</p>
                          <p className="text-sm text-slate-500">{rev.revision_date}</p>
                        </div>
                        {rev.description && (
                          <p className="text-sm text-slate-600">{rev.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {rev.is_active ? (
                          <Badge className="bg-blue-100 text-blue-700">Active</Badge>
                        ) : hasRole('admin', 'quality') && (
                          <Button size="sm" variant="outline" onClick={() => handleSetActiveRevision(rev)}>
                            Set Active
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="work-cycle" className="mt-6">
          <div className="space-y-6">
            {/* Work Cycles */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Work Cycles</CardTitle>
                {hasRole('admin', 'quality', 'production') && activeRevision && (
                  <Button size="sm" onClick={() => setCycleDialogOpen(true)} data-testid="add-cycle-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Cycle
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {workCycles.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No work cycles defined</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {workCycles.map((cycle) => (
                      <Button
                        key={cycle.id}
                        variant={selectedCycleId === cycle.id ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setSelectedCycleId(cycle.id);
                          loadWorkPhases(cycle.id);
                        }}
                      >
                        {cycle.code}
                      </Button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Work Phases */}
            {selectedCycleId && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Work Phases</CardTitle>
                  {hasRole('admin', 'quality', 'production') && (
                    <Button size="sm" onClick={() => setPhaseDialogOpen(true)} data-testid="add-phase-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Phase
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {workPhases.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No phases defined</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th className="w-16">#</th>
                            <th>Descrizione</th>
                            <th>Reparto</th>
                            <th>Macchina</th>
                            <th className="w-24">Tempo Std.</th>
                            <th>Controllo</th>
                            <th className="w-24">Azioni</th>
                          </tr>
                        </thead>
                        <tbody>
                          {workPhases.map((phase) => (
                            editingPhaseId === phase.id ? (
                              <tr key={phase.id} className="bg-blue-50">
                                <td><Input type="number" className="w-16 h-8 text-sm" value={editPhaseForm.phase_number} onChange={(e) => setEditPhaseForm(f => ({...f, phase_number: e.target.value}))} data-testid="edit-phase-number" /></td>
                                <td><Input className="h-8 text-sm" value={editPhaseForm.description} onChange={(e) => setEditPhaseForm(f => ({...f, description: e.target.value}))} data-testid="edit-phase-desc" /></td>
                                <td><Input className="h-8 text-sm w-28" value={editPhaseForm.department} onChange={(e) => setEditPhaseForm(f => ({...f, department: e.target.value}))} data-testid="edit-phase-dept" /></td>
                                <td>
                                  <select value={editPhaseForm.machine_id} onChange={(e) => setEditPhaseForm(f => ({...f, machine_id: e.target.value}))}
                                    className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="edit-phase-machine">
                                    <option value="">Nessuna</option>
                                    {machines.map(m => <option key={m.id} value={m.id}>{`${m.machine_code} - ${m.machine_name}`}</option>)}
                                  </select>
                                </td>
                                <td><Input type="number" className="w-20 h-8 text-sm" value={editPhaseForm.standard_time_minutes} onChange={(e) => setEditPhaseForm(f => ({...f, standard_time_minutes: e.target.value}))} data-testid="edit-phase-time" /></td>
                                <td><Input className="h-8 text-sm" value={editPhaseForm.required_control} onChange={(e) => setEditPhaseForm(f => ({...f, required_control: e.target.value}))} data-testid="edit-phase-control" /></td>
                                <td>
                                  <div className="flex gap-1">
                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={saveEditPhase} disabled={phaseSaving} data-testid="save-edit-phase-btn"><Save className="w-3.5 h-3.5" /></Button>
                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500" onClick={cancelEditPhase} data-testid="cancel-edit-phase-btn"><X className="w-3.5 h-3.5" /></Button>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              <tr key={phase.id} className="group hover:bg-slate-50">
                                <td className="font-mono">{phase.phase_number}</td>
                                <td className="font-medium">{phase.description}</td>
                                <td>{phase.department || '-'}</td>
                                <td className="text-sm">{phase.machine_code ? `${phase.machine_code} - ${phase.machine_name || ''}` : phase.default_machine || '-'}</td>
                                <td className="text-sm">{phase.standard_time_minutes ? `${phase.standard_time_minutes} min` : '-'}</td>
                                <td className="text-sm">{phase.required_control || '-'}</td>
                                <td>
                                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-blue-600" onClick={() => startEditPhase(phase)} data-testid={`edit-phase-${phase.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => deletePhase(phase.id)} data-testid={`delete-phase-${phase.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                                  </div>
                                </td>
                              </tr>
                            )
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="control-plans" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Control Plans</CardTitle>
              <Link to="/control-plans">
                <Button size="sm" variant="outline">
                  Manage Control Plans
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {controlPlans.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No control plans defined</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Characteristic</th>
                        <th>Nominal</th>
                        <th>Tolerance</th>
                        <th>Method</th>
                        <th>Type</th>
                        <th>Stage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {controlPlans.map((plan) => (
                        <tr key={plan.id}>
                          <td className="font-mono text-sm">{plan.control_code}</td>
                          <td className="font-medium">{plan.characteristic}</td>
                          <td className="text-sm">{plan.nominal_value || '-'}</td>
                          <td className="text-sm font-mono">
                            {plan.tolerance_min && plan.tolerance_max ? `${plan.tolerance_min} / ${plan.tolerance_max}` : '-'}
                          </td>
                          <td className="text-sm">{plan.control_method || '-'}</td>
                          <td>
                            <Badge variant="outline">{plan.control_type}</Badge>
                          </td>
                          <td className="text-sm">{plan.control_stage}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tools" className="mt-6">
          {/* Hidden print area */}
          <div style={{ display: 'none' }}>
            <div ref={toolListPrintRef}>
              <PrintableToolList
                productCode={product?.code}
                productName={product?.name}
                revisionCode={activeRevision?.revision_code}
                tools={revisionTools}
              />
            </div>
          </div>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Lista Utensili - Rev. {activeRevision?.revision_code || '-'}</CardTitle>
              <div className="flex gap-2">
                {revisionTools.length > 0 && (
                  <Button variant="outline" size="sm" onClick={handlePrintToolList} data-testid="print-tool-list-btn">
                    <Printer className="w-4 h-4 mr-2" />Stampa
                  </Button>
                )}
                <Button size="sm" onClick={startAddTool} disabled={!!newToolRow} data-testid="add-tool-btn">
                  <Plus className="w-4 h-4 mr-2" />Aggiungi Utensile
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {revisionTools.length === 0 && !newToolRow ? (
                <div className="text-center py-8">
                  <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-3">Nessun utensile assegnato a questa revisione</p>
                  <Button variant="outline" onClick={startAddTool} data-testid="add-first-tool-btn">
                    <Plus className="w-4 h-4 mr-2" />Aggiungi il primo utensile
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table" data-testid="tool-list-table">
                    <thead>
                      <tr>
                        <th className="w-16">Seq.</th>
                        <th className="w-28">Codice</th>
                        <th>Descrizione</th>
                        <th className="w-36">Fase di Lavoro</th>
                        <th className="w-20">Altezza</th>
                        <th className="w-20">Raggio</th>
                        <th>Note</th>
                        <th className="w-20">Disegno</th>
                        <th className="w-24">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revisionTools.map((rt) => (
                        editingToolId === rt.id ? (
                          <tr key={rt.id} className="bg-blue-50">
                            <td><Input type="number" className="w-16 h-8 text-sm" value={editToolForm.sequence} onChange={(e) => setEditToolForm(f => ({...f, sequence: e.target.value}))} data-testid="edit-tool-seq" /></td>
                            <td><Input className="w-28 h-8 text-sm font-mono" value={editToolForm.tool_code} onChange={(e) => setEditToolForm(f => ({...f, tool_code: e.target.value}))} data-testid="edit-tool-code" /></td>
                            <td><Input className="h-8 text-sm" value={editToolForm.tool_description} onChange={(e) => setEditToolForm(f => ({...f, tool_description: e.target.value}))} data-testid="edit-tool-desc" /></td>
                            <td>
                              <Select value={editToolForm.work_phase_id || "none"} onValueChange={(v) => setEditToolForm(f => ({...f, work_phase_id: v === "none" ? "" : v}))}>
                                <SelectTrigger className="h-8 text-xs" data-testid="edit-tool-phase"><SelectValue placeholder="-" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">-- Nessuna --</SelectItem>
                                  {workPhases.map(p => <SelectItem key={p.id} value={p.id}>{p.phase_number} - {p.description}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </td>
                            <td><Input type="number" step="0.1" className="w-20 h-8 text-sm" value={editToolForm.height} onChange={(e) => setEditToolForm(f => ({...f, height: e.target.value}))} data-testid="edit-tool-height" /></td>
                            <td><Input type="number" step="0.01" className="w-20 h-8 text-sm" value={editToolForm.radius} onChange={(e) => setEditToolForm(f => ({...f, radius: e.target.value}))} data-testid="edit-tool-radius" /></td>
                            <td><Input className="h-8 text-sm" value={editToolForm.notes} onChange={(e) => setEditToolForm(f => ({...f, notes: e.target.value}))} data-testid="edit-tool-notes" /></td>
                            <td></td>
                            <td>
                              <div className="flex gap-1">
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={saveEditTool} disabled={toolSaving} data-testid="save-edit-tool-btn"><Save className="w-3.5 h-3.5" /></Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500" onClick={cancelEditTool} data-testid="cancel-edit-tool-btn"><X className="w-3.5 h-3.5" /></Button>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          <tr key={rt.id} className="group hover:bg-slate-50">
                            <td className="font-mono font-semibold">{rt.sequence}</td>
                            <td className="font-mono text-sm">{rt.tool_code || '-'}</td>
                            <td className="text-sm">{rt.tool_description || '-'}</td>
                            <td className="text-sm text-slate-600">{rt.phase_description || '-'}</td>
                            <td className="font-mono text-sm">{rt.height != null ? rt.height : '-'}</td>
                            <td className="font-mono text-sm">{rt.radius != null ? rt.radius : '-'}</td>
                            <td className="text-xs max-w-[200px] truncate">{rt.notes || '-'}</td>
                            <td>
                              {rt.drawing_url ? (
                                <div className="flex items-center gap-1">
                                  <button onClick={(e) => { e.stopPropagation(); setDrawingPreview(rt); }} className="w-8 h-8 rounded border border-slate-200 overflow-hidden hover:border-blue-400 transition-colors" data-testid={`view-drawing-${rt.id}`}>
                                    <img src={rt.drawing_url} alt="" className="w-full h-full object-cover" />
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); handleDrawingDelete(rt.id); }} className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
                                </div>
                              ) : (
                                <label className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`upload-drawing-${rt.id}`}>
                                  <Upload className="w-4 h-4 text-slate-400 hover:text-blue-500" />
                                  <input type="file" className="hidden" accept="image/*,.pdf,.dxf,.dwg" onChange={(e) => { if (e.target.files[0]) handleDrawingUpload(rt.id, e.target.files[0]); e.target.value=''; }} />
                                </label>
                              )}
                            </td>
                            <td>
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-blue-600" onClick={() => startEditTool(rt)} data-testid={`edit-tool-${rt.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => deleteTool(rt.id)} data-testid={`delete-tool-${rt.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                              </div>
                            </td>
                          </tr>
                        )
                      ))}
                      {/* New tool row */}
                      {newToolRow && (
                        <tr className="bg-emerald-50" data-testid="new-tool-row">
                          <td><Input type="number" className="w-16 h-8 text-sm" value={newToolRow.sequence} onChange={(e) => setNewToolRow(f => ({...f, sequence: e.target.value}))} data-testid="new-tool-seq" /></td>
                          <td><Input className="w-28 h-8 text-sm font-mono" placeholder="Codice" value={newToolRow.tool_code} onChange={(e) => setNewToolRow(f => ({...f, tool_code: e.target.value}))} data-testid="new-tool-code" /></td>
                          <td><Input className="h-8 text-sm" placeholder="Descrizione utensile" value={newToolRow.tool_description} onChange={(e) => setNewToolRow(f => ({...f, tool_description: e.target.value}))} data-testid="new-tool-desc" /></td>
                          <td>
                            <Select value={newToolRow.work_phase_id || "none"} onValueChange={(v) => setNewToolRow(f => ({...f, work_phase_id: v === "none" ? "" : v}))}>
                              <SelectTrigger className="h-8 text-xs" data-testid="new-tool-phase"><SelectValue placeholder="-" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">-- Nessuna --</SelectItem>
                                {workPhases.map(p => <SelectItem key={p.id} value={p.id}>{p.phase_number} - {p.description}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td><Input type="number" step="0.1" className="w-20 h-8 text-sm" placeholder="mm" value={newToolRow.height} onChange={(e) => setNewToolRow(f => ({...f, height: e.target.value}))} data-testid="new-tool-height" /></td>
                          <td><Input type="number" step="0.01" className="w-20 h-8 text-sm" placeholder="mm" value={newToolRow.radius} onChange={(e) => setNewToolRow(f => ({...f, radius: e.target.value}))} data-testid="new-tool-radius" /></td>
                          <td><Input className="h-8 text-sm" placeholder="Note" value={newToolRow.notes} onChange={(e) => setNewToolRow(f => ({...f, notes: e.target.value}))} data-testid="new-tool-notes" /></td>
                          <td></td>
                          <td>
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={saveNewTool} disabled={toolSaving} data-testid="save-new-tool-btn"><Save className="w-3.5 h-3.5" /></Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500" onClick={() => setNewToolRow(null)} data-testid="cancel-new-tool-btn"><X className="w-3.5 h-3.5" /></Button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Drawing Preview Dialog */}
        <Dialog open={!!drawingPreview} onOpenChange={() => setDrawingPreview(null)}>
          <DialogContent className="max-w-3xl">
            {drawingPreview && (
              <>
                <DialogHeader>
                  <DialogTitle>Disegno - {drawingPreview.tool_code || drawingPreview.tool_description || 'Utensile'}</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col items-center gap-4">
                  {drawingPreview.drawing_url?.endsWith('.pdf') ? (
                    <iframe src={drawingPreview.drawing_url} className="w-full h-[500px] border rounded-lg" title="Disegno PDF" />
                  ) : (
                    <img src={drawingPreview.drawing_url} alt="Disegno utensile" className="max-w-full max-h-[500px] rounded-lg border object-contain" />
                  )}
                  <div className="flex gap-2">
                    <a href={drawingPreview.drawing_url} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm"><Eye className="w-4 h-4 mr-2" />Apri Originale</Button>
                    </a>
                    <label className="cursor-pointer">
                      <Button variant="outline" size="sm" asChild><span><Upload className="w-4 h-4 mr-2" />Sostituisci</span></Button>
                      <input type="file" className="hidden" accept="image/*,.pdf,.dxf,.dwg" onChange={(e) => { if (e.target.files[0]) { handleDrawingUpload(drawingPreview.id, e.target.files[0]); setDrawingPreview(null); } e.target.value=''; }} />
                    </label>
                    <Button variant="destructive" size="sm" onClick={() => { handleDrawingDelete(drawingPreview.id); setDrawingPreview(null); }}><Trash2 className="w-4 h-4 mr-2" />Rimuovi</Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* ─── Configurations Tab ─── */}
        <TabsContent value="configurations" className="mt-6">
          <div className="space-y-6">
            {/* Configurations */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Configurazioni Prodotto (AS9100)</CardTitle>
                  {hasRole('admin', 'quality', 'production') && (
                    <Button size="sm" onClick={async () => {
                      setConfigForm({ drawing_revision: '', description: '', work_cycle_id: '', control_plan_id: '', tool_list_id: '', process_flow: '' });
                      try {
                        const wcRes = await workCycleService.getAll({ product_id: id });
                        setAllProductCycles(wcRes.data || []);
                      } catch { setAllProductCycles([]); }
                      setConfigDialogOpen(true);
                    }} data-testid="add-config-btn">
                      <Plus className="w-4 h-4 mr-1" />Nuova Configurazione
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {configurations.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">Nessuna configurazione definita per questo prodotto</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Codice</th>
                          <th>Rev. Disegno</th>
                          <th>Descrizione</th>
                          <th>Stato</th>
                          <th>Creata il</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {configurations.map(cfg => (
                          <tr key={cfg.id} className="row-link" onClick={() => navigate(`/configurations/${cfg.id}`)} data-testid={`config-row-${cfg.id}`}>
                            <td className="font-mono font-medium">{cfg.configuration_code}</td>
                            <td>{cfg.drawing_revision || '-'}</td>
                            <td className="max-w-[200px] truncate">{cfg.description || '-'}</td>
                            <td>
                              <Badge className={cfg.status === 'approvata' ? 'bg-emerald-500 text-white' : cfg.status === 'obsoleta' ? 'bg-slate-400 text-white' : 'bg-amber-500 text-white'}>
                                {cfg.status === 'approvata' ? 'Approvata' : cfg.status === 'obsoleta' ? 'Obsoleta' : 'Bozza'}
                              </Badge>
                              {cfg.status === 'approvata' && <Lock className="w-3 h-3 text-slate-400 inline ml-1" />}
                            </td>
                            <td className="text-sm">{cfg.created_at?.slice(0, 10)}</td>
                            <td onClick={e => e.stopPropagation()}>
                              {cfg.status === 'bozza' && hasRole('admin') && (
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={async () => { if (!window.confirm('Eliminare questa configurazione?')) return; try { await productConfigurationService.delete(cfg.id); toast.success('Eliminata'); productConfigurationService.getAll({ product_id: id }).then(r => setConfigurations(r.data)); } catch(e) { toast.error(e.response?.data?.detail || 'Errore'); } }}>
                                  <Trash2 className="w-4 h-4 text-rose-500" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tool Lists */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Liste Utensili Versionate</CardTitle>
                  {hasRole('admin', 'quality', 'production') && (
                    <Button size="sm" variant="outline" onClick={() => { setToolListForm({ description: '' }); setToolListDialogOpen(true); }} data-testid="add-toollist-btn">
                      <Plus className="w-4 h-4 mr-1" />Nuova Lista
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {productToolLists.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">Nessuna lista utensili definita</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Codice</th>
                          <th>Versione</th>
                          <th>Descrizione</th>
                          <th>Righe</th>
                          <th>Stato</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {productToolLists.map(tl => (
                          <tr key={tl.id} data-testid={`toollist-row-${tl.id}`}>
                            <td className="font-mono font-medium">{tl.tool_list_code}</td>
                            <td>V{tl.version}</td>
                            <td className="max-w-[200px] truncate">{tl.description || '-'}</td>
                            <td>{tl.lines?.length || 0}</td>
                            <td>
                              <Badge className={tl.status === 'approvata' ? 'bg-emerald-500 text-white' : tl.status === 'obsoleta' ? 'bg-slate-400 text-white' : 'bg-amber-500 text-white'}>
                                {tl.status === 'approvata' ? 'Approvata' : tl.status === 'obsoleta' ? 'Obsoleta' : 'Bozza'}
                              </Badge>
                            </td>
                            <td>
                              <div className="flex gap-1">
                                {tl.status === 'bozza' && hasRole('admin', 'quality') && (
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={async () => { if (!window.confirm('Approvare questa lista?')) return; try { await toolListService.update(tl.id, { status: 'approvata' }); toast.success('Approvata'); toolListService.getAll({ product_id: id }).then(r => setProductToolLists(r.data)); } catch(e) { toast.error(e.response?.data?.detail || 'Errore'); } }}>
                                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                  </Button>
                                )}
                                {tl.status === 'approvata' && hasRole('admin', 'quality') && (
                                  <Button variant="ghost" size="icon" className="h-8 w-8" title="Crea nuova versione" onClick={async () => { try { await toolListService.newVersion(tl.id); toast.success('Nuova versione creata'); toolListService.getAll({ product_id: id }).then(r => setProductToolLists(r.data)); } catch(e) { toast.error(e.response?.data?.detail || 'Errore'); } }}>
                                    <Copy className="w-4 h-4 text-blue-600" />
                                  </Button>
                                )}
                                {tl.status === 'bozza' && hasRole('admin') && (
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={async () => { if (!window.confirm('Eliminare?')) return; try { await toolListService.delete(tl.id); toast.success('Eliminata'); toolListService.getAll({ product_id: id }).then(r => setProductToolLists(r.data)); } catch(e) { toast.error(e.response?.data?.detail || 'Errore'); } }}>
                                    <Trash2 className="w-4 h-4 text-rose-500" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="attachments" className="mt-6">
          <FileUploadZone
            entityType="product"
            entityId={id}
            attachments={attachments}
            onUploadComplete={loadAttachments}
            onDeleteComplete={loadAttachments}
            canEdit={hasRole('admin', 'quality', 'production')}
          />
        </TabsContent>
      </Tabs>

      {/* Revision Dialog */}
      <Dialog open={revisionDialogOpen} onOpenChange={setRevisionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Revision</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateRevision}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Revision Code</Label>
                <Input
                  value={revisionForm.revision_code}
                  onChange={(e) => setRevisionForm({ ...revisionForm, revision_code: e.target.value })}
                  placeholder="REV-B"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Revision Date</Label>
                <Input
                  type="date"
                  value={revisionForm.revision_date}
                  onChange={(e) => setRevisionForm({ ...revisionForm, revision_date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={revisionForm.description}
                  onChange={(e) => setRevisionForm({ ...revisionForm, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={revisionForm.is_active}
                  onCheckedChange={(checked) => setRevisionForm({ ...revisionForm, is_active: checked })}
                />
                <Label>Set as active revision</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRevisionDialogOpen(false)}>Cancel</Button>
              <Button type="submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cycle Dialog */}
      <Dialog open={cycleDialogOpen} onOpenChange={setCycleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Work Cycle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCycle}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Cycle Code</Label>
                <Input
                  value={cycleForm.code}
                  onChange={(e) => setCycleForm({ ...cycleForm, code: e.target.value })}
                  placeholder="CYC-001"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={cycleForm.description}
                  onChange={(e) => setCycleForm({ ...cycleForm, description: e.target.value })}
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCycleDialogOpen(false)}>Cancel</Button>
              <Button type="submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Phase Dialog */}
      <Dialog open={phaseDialogOpen} onOpenChange={setPhaseDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Work Phase</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePhase}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phase Number</Label>
                  <Input
                    type="number"
                    value={phaseForm.phase_number}
                    onChange={(e) => setPhaseForm({ ...phaseForm, phase_number: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Std. Time (min)</Label>
                  <Input
                    type="number"
                    value={phaseForm.standard_time_minutes}
                    onChange={(e) => setPhaseForm({ ...phaseForm, standard_time_minutes: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={phaseForm.description}
                  onChange={(e) => setPhaseForm({ ...phaseForm, description: e.target.value })}
                  placeholder="Turning, Milling, etc."
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Input
                    value={phaseForm.department}
                    onChange={(e) => setPhaseForm({ ...phaseForm, department: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Macchina</Label>
                  <select
                    value={phaseForm.machine_id}
                    onChange={(e) => setPhaseForm({ ...phaseForm, machine_id: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid="phase-machine-select"
                  >
                    <option value="">Nessuna macchina</option>
                    {machines.map(m => (
                      <option key={m.id} value={m.id}>{`${m.machine_code} - ${m.machine_name}`}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Required Control</Label>
                <Input
                  value={phaseForm.required_control}
                  onChange={(e) => setPhaseForm({ ...phaseForm, required_control: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPhaseDialogOpen(false)}>Cancel</Button>
              <Button type="submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Configuration Creation Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Nuova Configurazione</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Revisione Disegno</Label>
              <Input value={configForm.drawing_revision} onChange={e => setConfigForm(f => ({ ...f, drawing_revision: e.target.value }))} data-testid="config-drawing-revision" />
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea value={configForm.description} onChange={e => setConfigForm(f => ({ ...f, description: e.target.value }))} rows={2} data-testid="config-description" />
            </div>
            <div className="space-y-2">
              <Label>Ciclo di Lavoro</Label>
              <Select value={configForm.work_cycle_id || 'none'} onValueChange={v => setConfigForm(f => ({ ...f, work_cycle_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="config-work-cycle"><SelectValue placeholder="Seleziona ciclo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {allProductCycles.map(wc => <SelectItem key={wc.id} value={wc.id}>{wc.code}{wc.description ? ` - ${wc.description}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
              {allProductCycles.length === 0 && <p className="text-xs text-slate-400">Nessun ciclo disponibile per questo prodotto. Creane uno nella tab "Ciclo di Lavoro".</p>}
            </div>
            <div className="space-y-2">
              <Label>Piano di Controllo</Label>
              <Select value={configForm.control_plan_id || 'none'} onValueChange={v => setConfigForm(f => ({ ...f, control_plan_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="config-control-plan"><SelectValue placeholder="Seleziona piano" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {controlPlans.map(cp => <SelectItem key={cp.id} value={cp.id}>{cp.plan_code || cp.control_code}{cp.characteristic ? ` - ${cp.characteristic}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Lista Utensili</Label>
              <Select value={configForm.tool_list_id || 'none'} onValueChange={v => setConfigForm(f => ({ ...f, tool_list_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="config-tool-list"><SelectValue placeholder="Seleziona lista" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna</SelectItem>
                  {productToolLists.map(tl => <SelectItem key={tl.id} value={tl.id}>{`${tl.tool_list_code} (V${tl.version})`}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Flusso di Processo</Label>
              <Textarea value={configForm.process_flow} onChange={e => setConfigForm(f => ({ ...f, process_flow: e.target.value }))} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>Annulla</Button>
            <Button onClick={async () => { try { await productConfigurationService.create({ product_id: id, ...configForm, work_cycle_id: configForm.work_cycle_id || null, control_plan_id: configForm.control_plan_id || null, tool_list_id: configForm.tool_list_id || null }); toast.success('Configurazione creata'); setConfigDialogOpen(false); productConfigurationService.getAll({ product_id: id }).then(r => setConfigurations(r.data)); } catch(e) { toast.error(e.response?.data?.detail || 'Errore'); } }} data-testid="save-config-btn">Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tool List Creation Dialog */}
      <Dialog open={toolListDialogOpen} onOpenChange={setToolListDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuova Lista Utensili</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea value={toolListForm.description} onChange={e => setToolListForm(f => ({ ...f, description: e.target.value }))} rows={2} data-testid="toollist-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToolListDialogOpen(false)}>Annulla</Button>
            <Button onClick={async () => { try { await toolListService.create({ product_id: id, ...toolListForm }); toast.success('Lista utensili creata'); setToolListDialogOpen(false); toolListService.getAll({ product_id: id }).then(r => setProductToolLists(r.data)); } catch(e) { toast.error(e.response?.data?.detail || 'Errore'); } }} data-testid="save-toollist-btn">Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
